import { hsl, interpolateHsl, interpolateRgbBasis, scaleSequential } from 'd3';
import { ColorVarsEnum } from 'enums/ColorVarsEnum';
import isEqual from 'lodash/isEqual';
import { ColorSlider, ColorSliderProps } from 'modules/ui/colors/ColorSlider';
import { getHls } from 'modules/ui/colors/HlsColorEditor/constants';
import { DisableHlsInterface } from 'modules/ui/colors/HlsColorEditor/types';
import React, { useEffect, useMemo, useState } from 'react';
import { FlexContainer } from 'styles/FlexContainer';
import { PrimaryTextSpan } from 'styles/TextsElements';
import { OnValueChange } from 'types/global';
import { ColorEditType, HexAndHLSColorInterface, HlsColorInterface } from 'types/store';

interface ValueContainerProps
  extends OnValueChange<number>,
    Pick<ColorSliderProps, 'min' | 'max' | 'step' | 'colorFn' | 'disabled'> {
  title: string;
}

const ValueContainer = ({ value, onChange, title, ...sliderProps }: ValueContainerProps) => {
  return (
    <FlexContainer width="100%" gap="5px" alignItems="center">
      <FlexContainer width="55px">
        <PrimaryTextSpan whiteSpace="nowrap" lineHeight="10px" color={`var(${ColorVarsEnum.Level_3})`}>
          {title}: {Math.floor(value)}
        </PrimaryTextSpan>
      </FlexContainer>
      <ColorSlider width={257} value={value} onChange={onChange} {...sliderProps} />
    </FlexContainer>
  );
};

export interface HlsColorEditorProps extends DisableHlsInterface {
  value: HexAndHLSColorInterface;
  onChange: (color: HexAndHLSColorInterface, type: ColorEditType) => void;
}

export const HlsColorEditor = ({ value, onChange, disableHls }: HlsColorEditorProps) => {
  const hlsValue = useMemo(() => ({ h: value.h, s: value.s, l: value.l }), [value]);

  const [localHlsValues, setLocalHlsValues] = useState<HlsColorInterface>(hlsValue);

  const { h, s, l } = localHlsValues;

  const hColorFn = scaleSequential((t) => hsl(t * 360, s / 100, l / 100).toString());
  const sColorFn = interpolateHsl(hsl(0, 0, l / 100).hex(), hsl(h, 1, l / 100).hex());
  const lColorFn = interpolateRgbBasis(['black', hsl(h, s / 100, l / 100).formatHex(), 'white']);

  const isChangedLocalHls = !isEqual(hlsValue, localHlsValues);

  useEffect(() => {
    setLocalHlsValues(hlsValue);
  }, [hlsValue]);

  useEffect(() => {
    if (isChangedLocalHls) {
      onChange({ ...value, h, hex: getHls({ ...hlsValue, h }) }, 'h');
    }
    /* TODO: check this no deps */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [h]);

  useEffect(() => {
    if (isChangedLocalHls) {
      onChange({ ...value, s, hex: getHls({ ...hlsValue, s }) }, 's');
    }
    /* TODO: check this no deps */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s]);

  useEffect(() => {
    if (isChangedLocalHls) {
      onChange({ ...value, l, hex: getHls({ ...hlsValue, l }) }, 'l');
    }
    /* TODO: check this no deps */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [l]);

  return (
    <>
      <FlexContainer flexDirection="column" width="100%">
        <ValueContainer
          value={h}
          title="H"
          onChange={(h) => setLocalHlsValues((currentValue) => ({ ...currentValue, h }))}
          max={360}
          step={0.0001}
          colorFn={hColorFn}
          disabled={disableHls?.h}
        />
        <ValueContainer
          value={s}
          step={0.0001}
          title="S"
          onChange={(s) => setLocalHlsValues((currentValue) => ({ ...currentValue, s }))}
          colorFn={sColorFn}
          disabled={disableHls?.s}
        />
        <ValueContainer
          value={l}
          step={0.0001}
          title="L"
          onChange={(l) => setLocalHlsValues((currentValue) => ({ ...currentValue, l }))}
          colorFn={lColorFn}
          disabled={disableHls?.l}
        />
      </FlexContainer>
    </>
  );
};
